import React, { useMemo, useState } from "react";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import {
  filterStaffRows,
  getStaffDisplayName,
} from "../SiteStaffAssignmentPanel/SiteStaffAssignmentPanel";
import "./StudyAssignedStaffPanel.scss";

export const getStaffSiteBadgesForStudy = (staff, selectedSites = []) => {
  if (!staff || !selectedSites.length) return [];

  const mappedIds = String(staff.MappedSiteIds || "")
    .split(",")
    .map((id) => id.trim().toLowerCase())
    .filter(Boolean);

  return selectedSites
    .filter((site) =>
      mappedIds.includes(String(site.value).trim().toLowerCase()),
    )
    .map((site) => ({
      id: site.value,
      label:
        site.site?.SiteAbbr ||
        site.site?.SiteName ||
        String(site.label || "")
          .replace(/\s*\([^)]*\)\s*$/, "")
          .trim() ||
        "Site",
    }));
};

const STATUS_FILTER = {
  ALL: "all",
  ADDED: "added",
  NOT_ADDED: "notAdded",
};

const StudyAssignedStaffPanel = ({
  staffRows = [],
  selectedSites = [],
  assignedStaffIds = [],
  onToggleStaff,
  hasSitesSelected = false,
  searchPlaceholder = "Search staff...",
  readOnly = false,
  className = "",
}) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER.ALL);

  const assignedIdSet = useMemo(
    () =>
      new Set(
        (assignedStaffIds || [])
          .map((id) => String(id).trim().toLowerCase())
          .filter(Boolean),
      ),
    [assignedStaffIds],
  );

  const searchFilteredStaff = useMemo(
    () => filterStaffRows(staffRows, search),
    [staffRows, search],
  );

  const filteredStaff = useMemo(() => {
    if (statusFilter === STATUS_FILTER.ALL) return searchFilteredStaff;
    return searchFilteredStaff.filter((row) => {
      const isAdded = assignedIdSet.has(
        String(row.StaffId).trim().toLowerCase(),
      );
      if (statusFilter === STATUS_FILTER.ADDED) return isAdded;
      if (statusFilter === STATUS_FILTER.NOT_ADDED) return !isAdded;
      return true;
    });
  }, [searchFilteredStaff, statusFilter, assignedIdSet]);

  const toggleStatusFilter = (filterKey) => {
    setStatusFilter((prev) => (prev === filterKey ? STATUS_FILTER.ALL : filterKey));
  };

  const emptyMessage = (() => {
    if (staffRows.length === 0) {
      return "No staff mapped to the selected site(s).";
    }
    if (searchFilteredStaff.length === 0) {
      return "No matching staff.";
    }
    if (statusFilter === STATUS_FILTER.ADDED) {
      return "No staff added to this study.";
    }
    if (statusFilter === STATUS_FILTER.NOT_ADDED) {
      return "No staff waiting to be added.";
    }
    return "No matching staff.";
  })();

  return (
    <aside
      className={`study-assigned-staff-panel ${className}`.trim()}
      aria-label="Study staff assignment"
    >
      <div className="study-assigned-staff-panel__toolbar">
        <div
          className="study-assigned-staff-panel__legend"
          aria-label="Staff assignment status filters"
          role="group"
        >
          <button
            type="button"
            className={`study-assigned-staff-panel__legend-item${
              statusFilter === STATUS_FILTER.ADDED ? " is-active" : ""
            }`}
            onClick={() => toggleStatusFilter(STATUS_FILTER.ADDED)}
            aria-pressed={statusFilter === STATUS_FILTER.ADDED}
          >
            <span className="study-assigned-staff-panel__legend-avatar">
              <span className="study-assigned-staff-panel__legend-initials">AD</span>
              <span className="study-assigned-staff-panel__legend-badge is-added">
                <i className="fas fa-check-circle" aria-hidden="true" />
              </span>
            </span>
            <span>Added to study</span>
          </button>
          <button
            type="button"
            className={`study-assigned-staff-panel__legend-item${
              statusFilter === STATUS_FILTER.NOT_ADDED ? " is-active" : ""
            }`}
            onClick={() => toggleStatusFilter(STATUS_FILTER.NOT_ADDED)}
            aria-pressed={statusFilter === STATUS_FILTER.NOT_ADDED}
          >
            <span className="study-assigned-staff-panel__legend-avatar">
              <span className="study-assigned-staff-panel__legend-initials">AD</span>
              <span className="study-assigned-staff-panel__legend-badge is-not-added">
                <i className="fas fa-ban" aria-hidden="true" />
              </span>
            </span>
            <span>Not added</span>
          </button>
        </div>
        <div className="study-assigned-staff-panel__search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!hasSitesSelected}
          />
        </div>
      </div>

      <div className="study-assigned-staff-panel__list">
        {!hasSitesSelected ? (
          <div className="study-assigned-staff-panel__empty">
            <p className="study-assigned-staff-panel__empty-text">
              Select site(s) to load staff.
            </p>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="study-assigned-staff-panel__empty">
            <p className="study-assigned-staff-panel__empty-text">
              {emptyMessage}
            </p>
          </div>
        ) : (
          filteredStaff.map((row) => {
            const name = getStaffDisplayName(row);
            const badges = getStaffSiteBadgesForStudy(row, selectedSites);
            const isAdded = assignedIdSet.has(
              String(row.StaffId).trim().toLowerCase(),
            );

            return (
              <div
                className={`study-staff-row${isAdded ? " is-added" : ""}`}
                key={row.StaffId}
              >
                <div className="study-staff-row__avatar-wrap">
                  <span
                    className="study-staff-row__avatar"
                    style={{ backgroundColor: getAvatarColor(name) }}
                    title={name}
                  >
                    {getInitials(name)}
                  </span>
                  <span
                    className={`study-staff-row__avatar-badge${
                      isAdded ? " is-added" : " is-not-added"
                    }`}
                    title={isAdded ? "Added to study" : "Not added to study"}
                    aria-hidden="true"
                  >
                    <i
                      className={`fas ${
                        isAdded ? "fa-check-circle" : "fa-ban"
                      }`}
                    />
                  </span>
                </div>

                <div className="study-staff-row__main">
                  <div className="study-staff-row__name-line">
                    <span className="study-staff-row__name" title={name}>
                      {name}
                    </span>
                    {row.EmployeeNum ? (
                      <span className="study-staff-row__number">
                        #{row.EmployeeNum}
                      </span>
                    ) : null}
                  </div>
                  {badges.length > 0 ? (
                    <div className="study-staff-row__badges">
                      {badges.map((badge) => (
                        <span
                          className="study-staff-row__site-badge"
                          key={badge.id}
                          title={badge.label}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>

                {!readOnly && onToggleStaff ? (
                  <button
                    type="button"
                    className={`btn btn-sm study-staff-row__action${
                      isAdded
                        ? " btn-outline-danger"
                        : " btn-outline-primary"
                    }`}
                    onClick={() => onToggleStaff(row, !isAdded)}
                    aria-pressed={isAdded}
                    aria-label={
                      isAdded ? `Remove ${name} from study` : `Add ${name} to study`
                    }
                    title={isAdded ? "Remove" : "Add"}
                  >
                    <i
                      className={`fas ${isAdded ? "fa-times" : "fa-plus"}`}
                      aria-hidden="true"
                    />
                  </button>
                ) : null}
              </div>
            );
          })
        )}
        {hasSitesSelected && filteredStaff.length > 0 ? (
          <div className="study-assigned-staff-panel__list-end" aria-hidden="true" />
        ) : null}
      </div>
    </aside>
  );
};

export default StudyAssignedStaffPanel;
